import { Suspense, useEffect } from 'react';
import { Outlet, ScrollRestoration, useMatches } from 'react-router';
import { I18nextProvider, useTranslation } from 'react-i18next';
import type { Locale } from '@ceo/shared';
import { i18nFor } from '@/i18n';
import { LocaleContext } from '@/lib/locale';
import { PageLoader } from '@/components/ui/Feedback';
import { Header } from './Header';
import { Footer } from './Footer';
import styles from './SiteLayout.module.css';

export type RouteHandle = { locale?: Locale; hero?: boolean };

/** Public site chrome. `handle.hero === false` routes get a solid header strip and top spacing. */
/** First Tab stop on every page: jumps past the header straight to the content. */
function SkipLink() {
  const { t } = useTranslation();
  return (
    <a href="#main" className={styles.skip}>
      {t('common.skipToContent')}
    </a>
  );
}

export function SiteLayout({ locale }: { locale: Locale }) {
  const matches = useMatches();
  const hero = !matches.some((m) => (m.handle as RouteHandle | undefined)?.hero === false);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <I18nextProvider i18n={i18nFor(locale)}>
      <LocaleContext value={locale}>
        <SkipLink />
        <Header overlay={hero} />
        <main id="main" className={hero ? undefined : styles.noHero}>
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
        <Footer />
        <ScrollRestoration />
      </LocaleContext>
    </I18nextProvider>
  );
}
